/**
 * Parse a `@db.Date` value at LOCAL midnight.
 *
 * The wire shape matters here: Prisma reads a Postgres `date` back as a JS
 * `Date` pinned to UTC midnight, so the API serializes `validUntil`/`dueDate`
 * and friends as `"2026-09-30T00:00:00.000Z"` — not as a bare `"2026-09-30"`.
 * Handing that string to any instant formatter (`new Date(...)`, date-fns
 * `format`, `formatDateTimeInTimeZone`) renders it in a zone, and every zone
 * west of UTC lands on the PREVIOUS calendar day. The leading `YYYY-MM-DD` is
 * the whole value: take it verbatim and build a local-midnight Date from it.
 */
export function parseDateOnly(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
}

/**
 * The last day served before an end instant, as `YYYY-MM-DD`. A series set to
 * end "At period end" ends at the midnight that starts the next day (its
 * `cancelAt`), so its last day is the day before. The day is read from the
 * value's leading date, like {@link parseDateOnly}: that midnight in the
 * company's zone, or UTC midnight for an end stored before, both fall on it.
 */
export function lastDayBefore(value: string): string {
  const day = parseDateOnly(value);
  day.setDate(day.getDate() - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/**
 * Format a date-only value (`YYYY-MM-DD`, or the `YYYY-MM-DDT00:00:00.000Z`
 * the API sends for a `@db.Date` column) as a friendly label like
 * "Jun 23, 2026". Uses {@link parseDateOnly} (local midnight) so the calendar
 * day never shifts — do NOT use tz-instant formatters on date-only values.
 *
 * This is the ONLY implementation: surfaces that need a different empty-value
 * placeholder wrap it rather than re-deriving the formatting.
 */
export function formatDateOnly(value: string): string {
  // eslint-disable-next-line no-restricted-syntax -- parseDateOnly returns a local-midnight Date for a date-only value; local rendering IS the intended calendar day (no tz instant involved).
  return parseDateOnly(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
